// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import type { File as S3File } from '@/models';
import { API_BASE, S3_ENDPOINT_BUCKET } from '@/config';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { UploadFilesForm } from '@/components/files/upload-files-s3-presigned-form.tsx';

const formSchema = z.object({
  name: z.string().min(1, {
    message: 'Server name is required.',
  }),
  imageUrl: z.string().optional(),
  description: z.string().optional(),
  isPrivate: z.boolean(),
});

export const EditServerModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const [fileUrl, setFileUrl] = useState('');
  const { server } = data;
  const socket = useSocket();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: server?.name || '',
      imageUrl: server?.imageUrl || '',
      description: server?.description || '',
      isPrivate: server?.isPrivate || false,
    },
  });

  useEffect(() => {
    if (server) {
      form.setValue('name', server.name);
      form.setValue('imageUrl', server.imageUrl || '');
      form.setValue('description', server.description || '');
      form.setValue('isPrivate', server.isPrivate || false);
    }
  }, [server, form]);

  useEffect(() => {
    if (fileUrl) {
      form.setValue('imageUrl', fileUrl, { shouldDirty: true });
    }
  }, [fileUrl, form]);

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const imageChanged = fileUrl && fileUrl !== server?.imageUrl;
    const finalValues = {
      ...values,
      imageUrl: imageChanged ? fileUrl : server?.imageUrl,
    };

    try {
      const editedServer = await fetch(
        `${API_BASE}/api/servers/${server?.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(finalValues),
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await editedServer.json();
      socket?.send(
        JSON.stringify({
          type: 'server-edit',
          payload: data,
        })
      );
      onClose();
      form.reset();
    } catch {
      // TODO: translate
      toast.error('Something went wrong. Cant edit server.');
    }
  };

  const onSubmitImage = async (savedFiles: S3File[]) => {
    const fileUrl = S3_ENDPOINT_BUCKET + '/' + savedFiles[0].fileName;
    setFileUrl(fileUrl);
    form.setValue('imageUrl', fileUrl, { shouldDirty: true });
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="text-center text-2xl font-bold">
            {t('EditServerModal.title')}
          </div>
          <div className="text-center">{t('EditServerModal.description')}</div>
        </ModalHeader>
        <ModalBody>
          <UploadFilesForm
            folderName="serverImage"
            onUploadSuccess={() => {}}
            onFilesSaved={onSubmitImage}
          />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="space-y-2 px-6">
                <div className="flex items-center justify-center text-center">
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center gap-1">
                        <h3>{t('edit_server_modal.current_image')}:</h3>
                        <img
                          key={field.value}
                          src={field.value}
                          width={128}
                          height={128}
                          className="rounded-md"
                          alt="Server Image"
                        />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase">
                        {t('EditServerModal.name')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={isLoading}
                          placeholder={t('EditServerModal.placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase">
                        Описание
                      </FormLabel>
                      <FormControl>
                        <Input
                          disabled={isLoading}
                          placeholder={t('EditServerModal.placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isPrivate"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-start gap-2">
                      <FormLabel className="text-xs font-bold uppercase">
                        {t('EditServerModal.private')}
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <ModalFooter>
                <Button
                  color="primary"
                  variant="primary"
                  disabled={isLoading}
                  type="submit"
                >
                  {t('EditServerModal.save')}
                </Button>
              </ModalFooter>
            </form>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
